/**
 * Play the week out — pick every remaining result, see where you land.
 *
 * Scott's ask: a simple way to run through the week and predict the flights.
 * One tap on a side calls the win, a second tap on the same side makes it a
 * halve, a third clears it. A quota round is picked by tapping names in
 * predicted finishing order. The projection panel keeps score the whole time:
 * real points first, picked points on top, flights sliced off the total.
 *
 * The picks are one phone's daydream. They live in localStorage under this
 * tournament's id — never in the document, never synced — and anything that
 * has genuinely settled refuses to be re-picked: the simulator's floor is
 * always the ledger.
 */

import { useEffect, useState } from 'react'
import { matchNameIn } from '../engine/naming'
import { matchCounts } from '../engine/types'
import type { Match, Pairing, Tournament } from '../engine/types'
import {
  flightOutlook,
  type FlightOutlookRow,
  EMPTY_PICKS,
  matchesInPlayOrder,
  pathToFlightOne,
  playerName,
  simulateSeeding,
  type SimPicks,
  type TournamentView,
} from '../select'
import { predictOutcomes, scoresThrough } from '../engine/model'
import { mergedScores, useStore } from '../store'
import { Bar, SectionRule } from '../ui'

const storageKey = (docId: string) => `woodsmen-sim-${docId}`

function loadPicks(docId: string): SimPicks {
  try {
    const raw = localStorage.getItem(storageKey(docId))
    if (!raw) return EMPTY_PICKS
    const parsed = JSON.parse(raw) as SimPicks
    return { contests: parsed.contests ?? {}, quota: parsed.quota ?? {} }
  } catch {
    return EMPTY_PICKS
  }
}

/** One head-to-head contest: tap a side once to win, again to halve, again to clear. */
function ContestPick({
  doc,
  pairing,
  pick,
  onPick,
}: {
  doc: Tournament
  pairing: Pairing
  pick: 'a' | 'b' | 'half' | undefined
  onPick: (next: 'a' | 'b' | 'half' | undefined) => void
}) {
  const tap = (side: 'a' | 'b') => {
    if (pick === side) onPick('half')
    else if (pick === 'half') onPick(undefined)
    else onPick(side)
  }
  const sideBtn = (side: 'a' | 'b') => {
    const names = pairing.sides[side === 'a' ? 0 : 1]!.playerIds
      .map((id) => playerName(doc, id))
      .join(' & ')
    const won = pick === side
    const halved = pick === 'half'
    return (
      <button
        type="button"
        onClick={() => tap(side)}
        aria-pressed={won}
        className={`bc-tap flex-1 min-w-0 px-2 py-1.5 border text-sm leading-snug ${
          won
            ? 'bg-ink text-paper border-ink'
            : halved
              ? 'bg-wash border-rule'
              : 'bg-paper border-rule'
        }`}
      >
        <span className="bc-name block truncate">{names}</span>
        <span className={`text-[11px] block ${won ? 'text-paper/70' : 'text-mute'}`}>
          {won ? 'WINS' : halved ? 'HALVED' : ' '}
        </span>
      </button>
    )
  }
  return (
    <div className="flex items-stretch gap-2 py-1">
      {sideBtn('a')}
      <span className="bc-label text-mute self-center">v</span>
      {sideBtn('b')}
    </div>
  )
}

/** A quota field: tap names in predicted finishing order; tap again to remove. */
function QuotaPick({
  doc,
  playerIds,
  order,
  onOrder,
}: {
  doc: Tournament
  playerIds: string[]
  order: string[]
  onOrder: (next: string[]) => void
}) {
  return (
    <div className="flex flex-wrap gap-2 py-1">
      {playerIds.map((pid) => {
        const place = order.indexOf(pid)
        return (
          <button
            key={pid}
            type="button"
            aria-pressed={place >= 0}
            onClick={() =>
              onOrder(place >= 0 ? order.filter((x) => x !== pid) : [...order, pid])
            }
            className={`bc-tap px-2.5 py-1 border text-sm ${
              place >= 0 ? 'bg-ink text-paper border-ink' : 'bg-paper border-rule'
            }`}
          >
            {place >= 0 && <span className="bc-num text-[12px] pr-1.5">{place + 1}</span>}
            <span className="bc-name">{playerName(doc, pid)}</span>
          </button>
        )
      })}
    </div>
  )
}

export function Simulate({
  doc,
  view,
  onBack,
}: {
  doc: Tournament
  view: TournamentView
  onBack: () => void
}) {
  const me = useStore((s) => s.session?.playerId ?? '')
  const scores = useStore(mergedScores)
  const [picks, setPicks] = useState<SimPicks>(() => loadPicks(doc.id))
  useEffect(() => {
    try {
      localStorage.setItem(storageKey(doc.id), JSON.stringify(picks))
    } catch {
      // A full disk loses nothing but a daydream.
    }
  }, [picks, doc.id])

  const sim = simulateSeeding(doc, view, picks)
  const championship = doc.matches.find(
    (m) => m.config.kind === 'strokeNet' && m.config.flighted,
  )

  /*
   * "Flight 1" — one tap fills the picks with a road that gets YOU into the
   * top flight, if one exists on results alone, and says which story it is:
   * win out while the field holds serve, or win out while the leaders go
   * cold. The picks land in the ordinary editor, so the road is inspectable
   * and arguable, not an oracle. See `pathToFlightOne`.
   */
  const [story, setStory] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [view2, setView2] = useState<'picks' | 'alive'>('picks')

  /*
   * The likely week, not a possible one. Runs the real engine over a few
   * hundred simulated finishes and takes each contest's modal winner —
   * enough to be stable on twelve men, few enough to keep a thumb waiting
   * under a second. Deferred a frame so the button can say "Playing…"
   * before the work starts.
   */
  const predict = async () => {
    setBusy(true)
    setStory(null)
    await new Promise((r) => setTimeout(r, 0))
    try {
      const known = scoresThrough(doc, scores, null)
      const out = predictOutcomes(doc, known, { sims: 240, seedKey: 'predict' })
      setPicks({ contests: out.contests, quota: out.quota })
      const halves = Object.values(out.contests).filter((v) => v === 'half').length
      setStory(
        `The model's week: every open contest played out 240 times, and the side that wins most often takes it${
          halves > 0 ? ` — ${halves} too close to call, left halved` : ''
        }. Tap any of it to argue.`,
      )
    } finally {
      setBusy(false)
    }
  }

  const mapMyPath = () => {
    if (!me) return
    const path = pathToFlightOne(doc, view, me)
    if ('picks' in path) {
      setPicks(path.picks)
      setStory(
        path.flavor === 'held'
          ? `Win everything still open to you and let every other match halve — you land seed ${path.position}. Flight 1 on your own golf.`
          : `Winning out is not enough on halves — but if the men above you eat the bottom of every remaining ladder, seed ${path.position} is yours. You need help, but the door is open.`,
      )
    } else {
      setStory(
        `No road on results alone: even winning out with the leaders ice cold, you finish seed ${path.position}. Time to befriend Flight ${path.position <= 8 ? 2 : 3}.`,
      )
    }
  }

  /*
   * Only what is still up for grabs. A match every card has settled offers
   * nothing to pick, and a settled CONTEST inside a live match is shown by
   * the ledger, not re-litigated here.
   */
  const simmable = matchesInPlayOrder(doc).filter(
    (m) => matchCounts(m) && m.id !== championship?.id && !view.finalMatchIds.has(m.id),
  )

  const pickCount =
    Object.keys(picks.contests).length +
    Object.values(picks.quota).reduce((s, o) => s + o.length, 0)

  const renderMatch = (m: Match) => {
    const c = m.config
    if (c.kind === 'quota') {
      const groups = doc.groups.filter((g) => g.roundId === m.roundId)
      const blocks =
        c.rankingScope === 'group'
          ? groups.map((g) => ({ key: `${m.id}~${g.id}`, playerIds: g.playerIds }))
          : [{ key: m.id, playerIds: groups.flatMap((g) => g.playerIds) }]
      return blocks.map((b) => (
        <div key={b.key}>
          <p className="text-[12px] text-mute pt-1">
            Tap in finishing order{c.rankingScope === 'field' ? ' — the whole field' : ''}:
          </p>
          <QuotaPick
            doc={doc}
            playerIds={b.playerIds.filter((pid) => !view.counted(m.id, pid))}
            order={(picks.quota[b.key] ?? []).filter((pid) =>
              b.playerIds.includes(pid),
            )}
            onOrder={(next) =>
              setPicks((p) => ({ ...p, quota: { ...p.quota, [b.key]: next } }))
            }
          />
        </div>
      ))
    }
    if (c.kind === 'strokeNet') return null
    const open = doc.pairings.filter(
      (p) =>
        p.matchId === m.id &&
        !p.sides.some((sd) => sd.playerIds.some((pid) => view.counted(m.id, pid))),
    )
    if (open.length === 0)
      return <p className="text-[12px] text-mute py-1">Every contest here has settled.</p>
    return open.map((p) => (
      <ContestPick
        key={p.id}
        doc={doc}
        pairing={p}
        pick={picks.contests[p.id]}
        onPick={(next) =>
          setPicks((prev) => {
            const contests = { ...prev.contests }
            if (next === undefined) delete contests[p.id]
            else contests[p.id] = next
            return { ...prev, contests }
          })
        }
      />
    ))
  }

  return (
    <>
      <Bar
        kicker={doc.name}
        title="Play it out"
        sub="One phone’s guess — nothing here is saved to the week"
      />
      {/*
        Two ways to ask the same question. The picks are a daydream you
        build; the outlook is arithmetic on what is already true — floors,
        ceilings, and who the numbers have already eliminated.
      */}
      <div className="grid grid-cols-2 border-b border-rule">
        {(['picks', 'alive'] as const).map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setView2(v)}
            className={`bc-label py-3 bc-tap ${
              view2 === v ? 'bg-ink text-paper' : 'bg-paper text-mute'
            }`}
          >
            {v === 'picks' ? 'Pick it out' : "Who's alive"}
          </button>
        ))}
      </div>

      {view2 === 'alive' ? (
        <FlightOutlook doc={doc} view={view} me={me} onBack={onBack} />
      ) : (
      <>
      <div className="flex items-center gap-2 px-4 py-2 border-b border-rule">
        <button type="button" onClick={onBack} className="bc-tap px-3 border border-rule bc-label">
          ‹ The week
        </button>
        <button
          type="button"
          onClick={() => {
            setPicks(EMPTY_PICKS)
            setStory(null)
          }}
          disabled={pickCount === 0}
          className="bc-tap px-3 border border-rule bc-label disabled:opacity-40"
        >
          Clear
        </button>
        {me && championship && simmable.length > 0 && (
          <button
            type="button"
            onClick={mapMyPath}
            className="bc-tap px-3 bc-label bg-ink text-paper"
          >
            Flight 1
          </button>
        )}
        {/*
          Flight 1 maps a road a man COULD walk; this fills in what the model
          thinks will actually happen. Same editor either way, so the answer
          is arguable rather than oracular.
        */}
        {simmable.length > 0 && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void predict()}
            className="bc-tap px-3 bc-label border-2 border-ink disabled:opacity-40"
          >
            {busy ? 'Playing…' : 'Simulate'}
          </button>
        )}
        <span className="text-[12px] text-mute ml-auto">
          {pickCount === 0 ? 'Nothing picked yet' : `${pickCount} pick${pickCount === 1 ? '' : 's'}`}
        </span>
      </div>

      {/*
        The answer first, and it STAYS first: the projection pins to the top
        while the picks scroll underneath, one column per flight, so every tap
        is answered in the same glance that made it — Scott's ask. Compact by
        necessity: three columns of four names has no room for anything but
        the name and the number.
      */}
      {story && (
        <p className="px-4 py-2.5 text-[13px] leading-snug border-b border-rule">
          <b className="bc-label text-signal">Your road&nbsp;&nbsp;</b>
          {story}
        </p>
      )}

      {sim.flights.length > 0 ? (
        <div className="sticky top-0 z-30 bg-paper border-b-2 border-ink px-3 pt-1.5 pb-2">
          <div
            className="grid gap-3"
            style={{ gridTemplateColumns: `repeat(${sim.flights.length}, minmax(0, 1fr))` }}
          >
            {sim.flights.map((f) => (
              <div key={f.name} className="min-w-0">
                <span className="bc-label block text-mute">{f.name.replace('Flight ', 'F')}</span>
                <ul>
                  {f.playerIds.map((pid) => {
                    const row = sim.rows.find((r) => r.playerId === pid)!
                    return (
                      <li
                        key={pid}
                        className={`flex items-baseline justify-between gap-1 text-[12px] leading-tight py-px ${
                          pid === me ? 'bc-name' : 'text-mute'
                        }`}
                      >
                        <span className="truncate min-w-0">
                          <span className="bc-num pr-1">{row.position}</span>
                          {playerName(doc, pid)}
                        </span>
                        <span className="bc-num tabular-nums shrink-0">
                          {row.settled + row.simmed}
                        </span>
                      </li>
                    )
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <p className="px-4 py-2 text-[13px] text-mute">No championship flights configured.</p>
      )}
      <p className="px-4 py-2 text-[12px] text-mute leading-snug border-b border-rule">
        Settled points plus your picks, nothing else. Projected ties keep today&rsquo;s
        seeding order — simulated golf has no cards for the tiebreakers to read.
      </p>

      {simmable.length === 0 ? (
        <p className="px-4 py-4 text-[13px] text-mute">
          Nothing left to pick — the week has settled.
        </p>
      ) : (
        simmable.map((m) => (
          <div key={m.id}>
            <SectionRule>{matchNameIn(doc, m)}</SectionRule>
            <div className="px-4 pb-2">{renderMatch(m)}</div>
          </div>
        ))
      )}
      </>
      )}
    </>
  )
}

/**
 * The whole field's road into Flight 1, by arithmetic rather than daydream.
 *
 * A man's floor is what he holds — nothing this week takes points back — and
 * his ceiling is that plus everything still winnable. Four floors above your
 * ceiling and the flight fills without you, which is the one verdict here
 * hard enough to print as ELIMINATED. Everything else is a gap, stated as a
 * gap, because how much has to go right is a judgment and the group can make
 * it themselves.
 */
function FlightOutlook({
  doc,
  view,
  me,
  onBack,
}: {
  doc: Tournament
  view: TournamentView
  me: string
  onBack: () => void
}) {
  const { rows, seats, upsideLeft } = flightOutlook(doc, view)
  const BANDS: { key: FlightOutlookRow['status']; title: string; note: string }[] = [
    { key: 'in', title: 'Holding a seat', note: `The top ${seats} as it stands` },
    { key: 'live', title: 'Live', note: 'One good day does it' },
    { key: 'help', title: 'Needs help', note: 'A win, and the men above going quiet' },
    { key: 'miracle', title: 'Miracle territory', note: 'Everything, and then the tiebreak' },
    { key: 'out', title: 'Out of it', note: 'The arithmetic has closed' },
  ]
  return (
    <>
      <div className="flex items-center gap-2 px-4 py-2 border-b border-rule">
        <button type="button" onClick={onBack} className="bc-tap px-3 border border-rule bc-label">
          ‹ The week
        </button>
        <span className="text-[12px] text-mute ml-auto">
          {upsideLeft} pts still winnable
        </span>
      </div>
      {upsideLeft === 0 ? (
        <p className="px-4 py-4 text-[13px] text-mute">
          Every counting match has settled — the flights are what they are.
        </p>
      ) : (
        BANDS.map((band) => {
          const men = rows.filter((r) => r.status === band.key)
          if (men.length === 0) return null
          return (
            <div key={band.key}>
              <div className="px-4 pt-3 pb-1 border-t border-rule">
                <span className="bc-label text-signal">{band.title}</span>
                <span className="text-[12px] text-mute"> · {band.note}</span>
              </div>
              <ul className="divide-y divide-rule border-t border-rule">
                {men.map((r) => (
                  <li
                    key={r.playerId}
                    className={`px-4 py-2 flex items-baseline justify-between gap-3 ${
                      r.playerId === me ? 'bg-signal/20' : ''
                    }`}
                  >
                    <span className="min-w-0">
                      <span className="bc-name text-sm">{playerName(doc, r.playerId)}</span>
                      <span className="text-[12px] text-mute">
                        {' '}
                        · {r.points} pts, up to {r.ceiling}
                      </span>
                    </span>
                    <span className="text-[12px] text-right shrink-0">
                      {r.status === 'in' ? (
                        <span className="text-mute">seed {r.position}</span>
                      ) : r.status === 'out' ? (
                        <span className="text-mute">
                          needs {r.gapToCut}, {r.upside} left
                        </span>
                      ) : (
                        <>
                          <span className="bc-num text-ink">+{r.gapToCut}</span>
                          <span className="text-mute"> on the cut</span>
                        </>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )
        })
      )}
      <p className="px-4 py-3 text-[12px] text-mute leading-snug">
        Points already banked cannot be lost, so a man&rsquo;s floor is what he holds and his
        ceiling is that plus everything still winnable. Four floors above a ceiling and the
        flight fills without him.
      </p>
    </>
  )
}
